import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { CATEGORIES, PRIORITIES, PRIORITY_META } from "../lib/constants";
import { uploadAttachment, formatBytes, isImage } from "../lib/attachments";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Copy, Send, Paperclip, X, ImageIcon } from "lucide-react";
import { Link } from "react-router-dom";

export default function SubmitTicket() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    subject: "",
    message: "",
    category: "general",
    priority: "medium",
  });
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [files, setFiles] = useState([]);

  const MAX_MB = 10;
  const MAX_FILES = 5;

  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    const combined = [...files];
    for (const f of incoming) {
      if (combined.length >= MAX_FILES) {
        toast.error(`Up to ${MAX_FILES} files per ticket`);
        break;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} is over ${MAX_MB} MB`);
        continue;
      }
      combined.push(f);
    }
    setFiles(combined);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/tickets", form);
      // upload attachments after ticket creation
      const attached = [];
      const failed = [];
      for (const f of files) {
        try {
          const a = await uploadAttachment(data.id, f);
          attached.push(a);
        } catch (err) {
          failed.push({ name: f.name, err: formatApiError(err) });
        }
      }
      if (failed.length) {
        toast.warning(`${failed.length} file(s) failed to upload`, {
          description: failed.map((x) => `${x.name}: ${x.err}`).join("\n"),
        });
      }
      setCreated({ ...data, attachments: attached });
      toast.success("Ticket created", { description: data.ticket_number });
    } catch (err) {
      toast.error("Could not create ticket", { description: formatApiError(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        <div className="beacon-card p-8 text-center shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center mx-auto border border-emerald-100">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h2 className="mt-5 text-2xl font-bold text-slate-900 font-display">Ticket received</h2>
          <p className="text-sm text-slate-600 mt-1">
            Save this reference — you'll need it (plus your email) to track status.
          </p>
          <div className="mt-6 mx-auto max-w-sm bg-slate-900 text-white rounded-xl px-5 py-4 flex items-center justify-between">
            <div className="font-mono text-lg font-bold tracking-widest" data-testid="submit-success-ticket-number">
              {created.ticket_number}
            </div>
            <button
              className="text-slate-300 hover:text-white transition-colors"
              onClick={() => {
                navigator.clipboard.writeText(created.ticket_number);
                toast.success("Copied");
              }}
              data-testid="copy-ticket-number-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <div className="mt-7 flex flex-wrap gap-2 justify-center">
            <Link
              to="/my-tickets"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all"
              data-testid="go-my-tickets-btn"
            >
              See my tickets
            </Link>
            <button
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-all"
              onClick={() => {
                setCreated(null);
                setFiles([]);
                setForm({ subject: "", message: "", category: "general", priority: "medium" });
              }}
              data-testid="submit-another-btn"
            >
              Submit another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight font-display">
          Tell us what's up.
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          We read every ticket — usually within a few hours.
        </p>
      </div>
      <form onSubmit={submit} className="beacon-card p-6 sm:p-8 shadow-xl space-y-5">
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-600">
            Submitting as{" "}
            <span className="font-semibold text-slate-900">{user.name}</span>{" "}
            <span className="font-mono text-slate-500">({user.email})</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="beacon-label">Category</label>
            <select className="beacon-input" data-testid="ticket-form-category-select"
              value={form.category} onChange={upd("category")}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="beacon-label">Priority</label>
            <select className="beacon-input" data-testid="ticket-form-priority-select"
              value={form.priority} onChange={upd("priority")}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{PRIORITY_META[p].label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="beacon-label">Subject</label>
          <input required className="beacon-input" data-testid="ticket-form-subject-input"
            value={form.subject} onChange={upd("subject")} placeholder="Short summary of the issue" />
        </div>

        <div>
          <label className="beacon-label">Message</label>
          <textarea required rows={6} className="beacon-input resize-y" data-testid="ticket-form-message-textarea"
            value={form.message} onChange={upd("message")}
            placeholder="Anything that helps us reproduce it — steps, screenshots links, error text..." />
        </div>

        <div>
          <label className="beacon-label flex items-center justify-between">
            <span>Attachments <span className="text-slate-400 font-normal">(optional · up to {MAX_FILES} files · {MAX_MB} MB each)</span></span>
          </label>
          <label
            data-testid="ticket-form-attach-dropzone"
            className="flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/40 rounded-xl px-4 py-6 cursor-pointer transition-colors"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
          >
            <Paperclip className="w-5 h-5 text-slate-500" />
            <div className="text-sm font-semibold text-slate-700">Click to add files or drop them here</div>
            <div className="text-[11px] text-slate-500">Screenshots (PNG/JPG), PDFs, or logs</div>
            <input
              type="file"
              multiple
              className="hidden"
              accept="image/*,application/pdf,text/plain,.log,.zip"
              data-testid="ticket-form-attach-input"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            />
          </label>

          {files.length > 0 && (
            <ul className="mt-3 space-y-2" data-testid="ticket-form-attach-list">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center shrink-0">
                    {f.type.startsWith("image/") ? <ImageIcon className="w-4 h-4 text-slate-500" /> : <Paperclip className="w-4 h-4 text-slate-500" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{f.name}</div>
                    <div className="text-[11px] text-slate-500">{formatBytes(f.size)} · {f.type || "file"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    data-testid={`ticket-form-attach-remove-${i}`}
                    className="text-slate-400 hover:text-rose-600 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="ticket-form-submit-btn"
          className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg transition-all active:scale-[0.99]"
        >
          <Send className="w-4 h-4 mr-2" />
          {submitting ? "Submitting..." : "Submit ticket"}
        </Button>
      </form>
    </div>
  );
}
